
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { OpportunityHeader } from "@/components/opportunity/OpportunityHeader";
import { OpportunityDetails } from "@/components/opportunity/OpportunityDetails";
import { OpportunityNotes } from "@/components/opportunity/OpportunityNotes";
import { OpportunityActivities } from "@/components/opportunity/OpportunityActivities";
import { useOpportunities } from "@/hooks/useOpportunities";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OpportunityWithRelations } from "@/types/opportunity.types";

export default function OpportunityDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { 
    getOpportunityById,
    deleteOpportunity,
    getOpportunityNotes,
    addOpportunityNote,
    getOpportunityActivities,
    logOpportunityActivity
  } = useOpportunities();
  
  const { 
    data: opportunityData, 
    isLoading,
    error
  } = getOpportunityById(id!);

  // Process the opportunity data to conform to the OpportunityWithRelations type
  const opportunity = opportunityData ? {
    ...opportunityData,
    // Format deal_champion as an object if it exists and is a string
    deal_champion: typeof opportunityData.deal_champion === 'string' && opportunityData.deal_champion 
      ? { 
          id: opportunityData.deal_champion,
          first_name: '',
          last_name: ''
        } 
      : opportunityData.deal_champion || undefined
  } as unknown as OpportunityWithRelations : undefined;

  const { 
    data: notes = [], 
    isLoading: isLoadingNotes 
  } = getOpportunityNotes(id!);

  const { 
    data: activities = [], 
    isLoading: isLoadingActivities 
  } = getOpportunityActivities(id!);

  useEffect(() => {
    if (error) {
      navigate("/opportunities");
    }
  }, [error, navigate]);

  const handleAddNote = (noteData: any) => {
    addOpportunityNote(noteData);
    
    // Log activity for adding a note
    if (id && user?.id) {
      logOpportunityActivity({
        opportunity_id: id,
        activity_type: "Note",
        description: "Added a note to the opportunity",
        performed_by: user.id
      });
    }
  };

  const handleDelete = (opportunityId: string) => {
    deleteOpportunity(opportunityId);
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-120px)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!opportunity) {
    return null;
  }

  return (
    <div className="space-y-6">
      <OpportunityHeader 
        opportunity={opportunity} 
        onDelete={handleDelete}
      />

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="mt-4">
          <OpportunityDetails opportunity={opportunity} />
        </TabsContent>
        
        <TabsContent value="notes" className="mt-4">
          <OpportunityNotes 
            opportunityId={id!}
            notes={notes}
            onAddNote={handleAddNote}
            isLoading={isLoadingNotes}
          />
        </TabsContent>
        
        <TabsContent value="activities" className="mt-4">
          <OpportunityActivities 
            activities={activities}
            isLoading={isLoadingActivities}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
